import { TableColumnSpec, TableRow } from './types';
import { normalizeRows } from './tableRows';

/**
 * Declaring a report's values once.
 *
 * A report used to write each field three times — in its values interface, in
 * `defaultValues`, and in `normalizeValues` with a hand-rolled coercer. A
 * schema says it once: the type, the default, and how stored JSON of any
 * earlier shape is brought up to it, with a label and help for whoever reads
 * the code. A report's values are what the report itself asks — remarks,
 * sign-offs — on its document; what is measured comes from a run or from an
 * input sheet (inputs.ts), not from here.
 *
 *   const values = defineValues({
 *     remarks: v.text({ label: 'Remarks', multiline: true }),
 *     status: v.choice(['provisional', 'released'] as const, { default: 'provisional' }),
 *     signoffs: v.map(v.text(), { hidden: true }),
 *   });
 *   type Values = ValuesOf<typeof values>;
 *
 * Pure: it runs in the browser and in the server sandbox.
 */

export type FieldKind =
  | 'text'
  | 'number'
  | 'boolean'
  | 'choice'
  | 'set'
  | 'table'
  | 'list'
  | 'map'
  | 'record'
  | 'layout'
  | 'dimensions'
  | 'bindings'
  | 'reading'
  | 'dimension'
  | 'json';

/** What a field is, beside its type: its label, help and constraints. JSON only, so it can cross the sandbox. */
export interface FieldMeta {
  kind: FieldKind;
  label?: string;
  help?: string;
  /** a text field edited as several lines */
  multiline?: boolean;
  /** may be left empty without the report counting it as missing */
  optional?: boolean;
  unit?: string;
  /** for choice and set fields */
  choices?: string[];
  choiceLabels?: Record<string, string>;
  /** for table fields */
  columns?: TableColumnSpec[];
  /** for record fields: the fields inside */
  fields?: Record<string, FieldMeta>;
  /** for list and map fields: what each entry is */
  item?: FieldMeta;
  /** the report manages it itself (sign-offs by role, block fields): not a field anyone fills in directly */
  hidden?: boolean;
  placeholder?: string;
}

export interface Field<T> {
  meta: FieldMeta;
  defaultValue: () => T;
  normalize: (raw: unknown) => T;
}

export type Fields = Record<string, Field<unknown>>;

/** The value type a schema's fields describe. */
export type ValuesOf<S> = S extends ValuesSchema<infer V> ? V : S extends Fields ? { [K in keyof S]: S[K] extends Field<infer T> ? T : never } : never;

type FieldValues<F extends Fields> = { [K in keyof F]: F[K] extends Field<infer T> ? T : never };

export interface ValuesSchema<V> {
  fields: Record<keyof V & string, Field<unknown>>;
  /** each field's form description, by key */
  meta: Record<string, FieldMeta>;
  defaults: () => V;
  /** Bring stored JSON of any earlier shape up to this one. Unknown keys are kept when the schema says so. */
  normalize: (raw: unknown) => V;
}

interface Common {
  label?: string;
  help?: string;
  optional?: boolean;
  hidden?: boolean;
  placeholder?: string;
}

const record = (raw: unknown): Record<string, unknown> => (raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {});
const clean = (meta: FieldMeta): FieldMeta => Object.fromEntries(Object.entries(meta).filter(([, v]) => v !== undefined)) as FieldMeta;

function field<T>(meta: FieldMeta, defaultValue: () => T, normalize: (raw: unknown) => T): Field<T> {
  return { meta: clean(meta), defaultValue, normalize };
}

function toNumber(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw.trim().replace('−', '-'));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export const v = {
  /** Free text; anything not a string becomes the default. */
  text(options: Common & { multiline?: boolean; default?: string } = {}): Field<string> {
    const fallback = options.default ?? '';
    return field({ kind: 'text', ...options, default: undefined } as FieldMeta, () => fallback, (raw) => (typeof raw === 'string' ? raw : typeof raw === 'number' ? String(raw) : fallback));
  },

  /** A number, or null while unset. Numeric strings are read as numbers. */
  number(options: Common & { unit?: string; default?: number | null } = {}): Field<number | null> {
    const fallback = options.default ?? null;
    return field({ kind: 'number', ...options, default: undefined } as FieldMeta, () => fallback, (raw) => (raw === null ? null : toNumber(raw) ?? fallback));
  },

  boolean(options: Common & { default?: boolean } = {}): Field<boolean> {
    const fallback = options.default ?? false;
    return field({ kind: 'boolean', ...options, default: undefined } as FieldMeta, () => fallback, (raw) => (typeof raw === 'boolean' ? raw : raw === 'true' ? true : raw === 'false' ? false : fallback));
  },

  /** One of a fixed set, or '' for none. A stored value outside the set becomes the default. */
  choice<C extends string>(choices: readonly C[], options: Common & { default?: C | ''; labels?: Record<string, string> } = {}): Field<C | ''> {
    const fallback = options.default ?? '';
    const { labels, ...rest } = options;
    return field(
      { kind: 'choice', ...rest, default: undefined, choices: [...choices], choiceLabels: labels } as FieldMeta,
      () => fallback,
      (raw) => (typeof raw === 'string' && (choices as readonly string[]).includes(raw) ? (raw as C) : raw === '' ? '' : fallback),
    );
  },

  /** Any number of a fixed set, each at most once, in the order stored. */
  set<C extends string>(choices: readonly C[], options: Common & { default?: C[]; labels?: Record<string, string> } = {}): Field<C[]> {
    const { labels, ...rest } = options;
    return field(
      { kind: 'set', ...rest, default: undefined, choices: [...choices], choiceLabels: labels } as FieldMeta,
      () => [...(options.default ?? [])],
      (raw) => (Array.isArray(raw) ? Array.from(new Set(raw.filter((c): c is C => (choices as readonly unknown[]).includes(c)))) : [...(options.default ?? [])]),
    );
  },

  /** Rows of typed cells, edited as a fillable table. */
  table(columns: TableColumnSpec[], options: Common & { default?: () => TableRow[] } = {}): Field<TableRow[]> {
    return field({ kind: 'table', ...options, default: undefined, columns } as FieldMeta, () => options.default?.() ?? [], (raw) => (Array.isArray(raw) ? normalizeRows(columns, raw) : options.default?.() ?? []));
  },

  /** A list of one kind of entry. */
  list<T>(item: Field<T>, options: Common = {}): Field<T[]> {
    return field({ kind: 'list', ...options, item: item.meta }, () => [], (raw) => (Array.isArray(raw) ? raw.map((r) => item.normalize(r)) : []));
  },

  /** Entries by string key, each of one kind. */
  map<T>(item: Field<T>, options: Common = {}): Field<Record<string, T>> {
    return field({ kind: 'map', ...options, item: item.meta }, () => ({}), (raw) => Object.fromEntries(Object.entries(record(raw)).map(([k, r]) => [k, item.normalize(r)])));
  },

  /** A nested group of fields. */
  record<F extends Fields>(fields: F, options: Common = {}): Field<FieldValues<F>> {
    const schema = defineValues(fields);
    return field({ kind: 'record', ...options, fields: schema.meta }, () => schema.defaults(), (raw) => schema.normalize(raw));
  },

  /** A field whose shape a module of the SDK owns (a layout, the dimensions, the bindings). */
  custom<T>(kind: FieldKind, defaultValue: () => T, normalize: (raw: unknown) => T, options: Common = {}): Field<T> {
    return field({ kind, ...options }, defaultValue, normalize);
  },

  /** Any JSON, kept as stored. For values a report manages itself. */
  json<T>(defaultValue: () => T, options: Common = {}): Field<T> {
    return field({ kind: 'json', hidden: true, ...options }, defaultValue, (raw) => (raw === undefined ? defaultValue() : (raw as T)));
  },
};

/**
 * A report's values (or settings, or a block's properties) from its fields.
 * `keepUnknown` carries stored keys the schema no longer names through
 * normalize, so a value an older version wrote is not lost on the next save.
 */
export function defineValues<F extends Fields>(fields: F, options: { keepUnknown?: boolean } = {}): ValuesSchema<FieldValues<F>> {
  type V = FieldValues<F>;
  const entries = Object.entries(fields) as Array<[string, Field<unknown>]>;
  return {
    fields: fields as unknown as ValuesSchema<V>['fields'],
    meta: Object.fromEntries(entries.map(([k, f]) => [k, f.meta])),
    defaults: () => Object.fromEntries(entries.map(([k, f]) => [k, f.defaultValue()])) as V,
    normalize: (raw) => {
      const source = record(raw);
      const known = Object.fromEntries(entries.map(([k, f]) => [k, source[k] === undefined ? f.defaultValue() : f.normalize(source[k])]));
      return (options.keepUnknown ? { ...source, ...known } : known) as V;
    },
  };
}

/** Whether a value counts as filled in, for the outline and for required fields. */
export function isFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
}
